import { useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { titleForPath, BRAND_TITLE_ROUTE, rememberBrandTitle } from './RouteTitle.jsx';

/**
 * @component MetaData
 * @description Side-effect orchestrator for browser metadata.
 *
 *   Keeps the *live* tab in sync with whatever branding is currently in the
 *   dictionary, so an edit in the admin shows up on the next page load without
 *   a rebuild. It does NOT help social crawlers — they don't run JS. The
 *   crawler-visible tags are baked into index.html at build time by the
 *   injectBrandMeta Vite plugin, which reads the same branding document from
 *   the dictionary's disk mirror.
 */
export default function MetaData() {
  const brand = useLanguage('branding');

  useEffect(() => {
    if (!brand) return;

    const meta = brand.META || {};

    // 1. Title — but only where the BRAND title is the right one.
    //
    // This effect re-runs whenever the branding document changes, and it used
    // to write the title on every route. So a dictionary response that landed
    // after a navigation reset the tab to the marketing tagline, undoing the
    // per-route name (walked 2026-09-09). RouteTitle owns the title on every
    // named route; the brand owns the pages where the tagline IS the title.
    const brandTitle = meta.TITLE || brand.PAGE?.TITLE || meta.NAME || 'Moksy Console';
    // Remembered either way: RouteTitle restores it when a navigation lands
    // back on the marketing root, where the brand owns the tab.
    rememberBrandTitle(brandTitle);
    if (BRAND_TITLE_ROUTE(window.location.pathname)) document.title = brandTitle;

    const setMeta = (name, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[name='${name}']`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const setIcon = (rel, href) => {
      if (!href) return;
      let link = document.querySelector(`link[rel='${rel}']`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // 2. Description + theme colour
    setMeta('description', meta.DESCRIPTION);
    setMeta('theme-color', meta.THEME_COLOR);

    // 3. Icons — the iOS home-screen icon falls back to the favicon when no
    //    dedicated touch icon is set.
    setIcon('icon', brand.LOGO?.FAVICON || brand.LOGO?.ICON);
    setIcon('apple-touch-icon', brand.LOGO?.TOUCH_ICON || brand.LOGO?.FAVICON || brand.LOGO?.ICON);
  }, [brand]);

  return null;
}
